import React, { useState, useEffect } from 'react'
import { LeadCreate, leadsApi } from '../services/api'
import api from '../services/api'

export interface LeadCreateOptions {
  createDeal?: { pipelineId: number; stageId: number; dealValue?: number }
  createContact?: boolean
  startConversation?: boolean
}

interface LeadFormProps {
  onSubmit: (data: any, options?: LeadCreateOptions) => void
  isLoading?: boolean
  initialData?: Partial<any>
  lead?: any
}

const LeadForm: React.FC<LeadFormProps> = ({ onSubmit, isLoading = false, initialData, lead }) => {
  const leadData = lead || initialData
  const [formData, setFormData] = useState<LeadCreate>({
    name: leadData?.name || '',
    email: leadData?.email || '',
    phone: leadData?.phone || '',
    company: leadData?.company || '',
    position: leadData?.position || '',
    avatar: leadData?.avatar || '',
    document_type: leadData?.document_type || 'none',
    cpf_cnpj: leadData?.cpf_cnpj || '',
    cep: leadData?.cep || '',
    logradouro: leadData?.logradouro || '',
    numero: leadData?.numero || '',
    complemento: leadData?.complemento || '',
    bairro: leadData?.bairro || '',
    cidade: leadData?.cidade || '',
    estado: leadData?.estado || '',
    pais: leadData?.pais || 'Brasil',
    source: leadData?.source || 'website',
    status: leadData?.status || 'new',
    priority: leadData?.priority || 'medium',
    notes: leadData?.notes || '',
    estimated_value: leadData?.estimated_value,
    probability: leadData?.probability,
    first_contact_date: leadData?.first_contact_date || undefined,
    follow_up_date: leadData?.follow_up_date || undefined,
    instagram_handle: leadData?.instagram_handle || '',
    facebook_url: leadData?.facebook_url || '',
    linkedin_url: leadData?.linkedin_url || '',
    custom_fields: leadData?.custom_fields || {},
    tags: leadData?.tags || []
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [showAdvanced, setShowAdvanced] = useState(false)

  // Pipeline / deal options
  const [createDeal, setCreateDeal] = useState(false)
  const [selectedPipelineId, setSelectedPipelineId] = useState<number | null>(null)
  const [selectedStageId, setSelectedStageId] = useState<number | null>(null)
  const [dealValue, setDealValue] = useState('')
  const [pipelines, setPipelines] = useState<any[]>([])
  const [stages, setStages] = useState<any[]>([])

  // Contact / conversation options (only meaningful in create mode)
  const [createContact, setCreateContact] = useState(false)
  const [startConversation, setStartConversation] = useState(false)

  // Custom field definitions
  const [customFieldDefs, setCustomFieldDefs] = useState<any[]>([])

  useEffect(() => {
    api.get('/pipelines/pipelines/').then(res => {
      const data = res.data
      setPipelines(Array.isArray(data) ? data : (data.results || []))
    }).catch(() => {})

    api.get('/leads/custom-fields/').then(res => {
      const data = res.data
      const list = Array.isArray(data) ? data : (data.results || [])
      setCustomFieldDefs(list.filter((f: any) => f.is_active).sort((a: any, b: any) => a.order - b.order))
    }).catch(() => {})
  }, [])

  useEffect(() => {
    if (!selectedPipelineId) { setStages([]); setSelectedStageId(null); return }
    api.get('/pipelines/stages/', { params: { pipeline: selectedPipelineId } }).then(res => {
      const data = res.data
      const list = Array.isArray(data) ? data : (data.results || [])
      const active = list.filter((s: any) => !s.is_won_stage && !s.is_lost_stage)
      setStages(active)
      if (active.length > 0) setSelectedStageId(active[0].id)
    }).catch(() => setStages([]))
  }, [selectedPipelineId])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target

    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? (value ? parseFloat(value) : undefined) : value
    }))

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  const handleCustomFieldChange = (fieldName: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      custom_fields: {
        ...(prev.custom_fields as Record<string, any>),
        [fieldName]: value
      }
    }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validation
    const newErrors: Record<string, string> = {}
    
    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório'
    }
    
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido'
    }
    
    if (formData.document_type === 'cpf' && formData.cpf_cnpj) {
      const cpf = formData.cpf_cnpj.replace(/\D/g, '')
      if (cpf.length !== 11) {
        newErrors.cpf_cnpj = 'CPF deve ter 11 dígitos'
      }
    }
    
    if (formData.document_type === 'cnpj' && formData.cpf_cnpj) {
      const cnpj = formData.cpf_cnpj.replace(/\D/g, '')
      if (cnpj.length !== 14) {
        newErrors.cpf_cnpj = 'CNPJ deve ter 14 dígitos'
      }
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }
    
    // Clean up data - remove empty strings and undefined values
    const cleanData: any = {}
    Object.entries(formData).forEach(([key, value]) => {
      if (value !== '' && value !== undefined && value !== null) {
        cleanData[key] = value
      }
    })
    
    const options: LeadCreateOptions = {}
    if (createDeal && selectedPipelineId && selectedStageId) {
      options.createDeal = {
        pipelineId: selectedPipelineId,
        stageId: selectedStageId,
        dealValue: dealValue ? parseFloat(dealValue) : undefined
      }
    }
    if (createContact) {
      options.createContact = true
      options.startConversation = startConversation
    }

    onSubmit(cleanData as LeadCreate, Object.keys(options).length > 0 ? options : undefined)
  }

  const handleCepSearch = async () => {
    const cepValue = formData.cep?.replace(/\D/g, '')
    if (cepValue && cepValue.length === 8) {
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cepValue}/json/`)
        const data = await response.json()
        
        if (!data.erro) {
          setFormData(prev => ({
            ...prev,
            logradouro: data.logradouro || '',
            bairro: data.bairro || '',
            cidade: data.localidade || '',
            estado: data.uf || '',
            complemento: data.complemento || ''
          }))
        }
      } catch (error) {
        console.error('Erro ao buscar CEP:', error)
      }
    }
  }

  const formatPhone = (value: string) => {
    const cleaned = value.replace(/\D/g, '')
    return cleaned.length === 11 
      ? `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`
      : cleaned
  }

  const formatCPF_CNPJ = (value: string) => {
    const cleaned = value.replace(/\D/g, '')
    if (formData.document_type === 'cpf') {
      return cleaned.length === 11 
        ? `${cleaned.slice(0, 3)}.${cleaned.slice(3, 6)}.${cleaned.slice(6, 9)}-${cleaned.slice(9)}`
        : cleaned
    } else if (formData.document_type === 'cnpj') {
      return cleaned.length === 14
        ? `${cleaned.slice(0, 2)}.${cleaned.slice(2, 5)}.${cleaned.slice(5, 8)}/${cleaned.slice(8, 12)}-${cleaned.slice(12)}`
        : cleaned
    }
    return value
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      {/* Basic Information */}
      <div>
        <h4 style={{
          fontSize: 'var(--text-sm)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--text-primary)',
          marginBottom: 'var(--space-4)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em'
        }}>
          Informações Básicas
        </h4>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'var(--space-4)' }}>
          <div>
            <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
              Nome <span style={{ color: 'var(--color-error-500)' }}>*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Nome completo"
              disabled={isLoading}
              style={{
                width: '100%',
                padding: 'var(--space-2) var(--space-3)',
                fontSize: 'var(--text-sm)',
                border: `1px solid ${errors.name ? 'var(--color-error-500)' : 'var(--border-medium)'}`,
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                transition: 'border-color var(--transition-fast)',
                opacity: isLoading ? 0.5 : 1
              }}
            />
            {errors.name && (
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-error-500)', marginTop: 'var(--space-1)' }}>
                {errors.name}
              </p>
            )}
          </div>
          
          <div>
            <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
              Email
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="email@exemplo.com"
              disabled={isLoading}
              style={{
                width: '100%',
                padding: 'var(--space-2) var(--space-3)',
                fontSize: 'var(--text-sm)',
                border: `1px solid ${errors.email ? 'var(--color-error-500)' : 'var(--border-medium)'}`,
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                transition: 'border-color var(--transition-fast)',
                opacity: isLoading ? 0.5 : 1
              }}
            />
            {errors.email && (
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-error-500)', marginTop: 'var(--space-1)' }}>
                {errors.email}
              </p>
            )}
          </div>
          
          <div>
            <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
              Telefone
            </label>
            <input
              type="text"
              name="phone"
              value={formatPhone(formData.phone || '')}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '')
                handleChange({ ...e, target: { ...e.target, name: 'phone', value } })
              }}
              placeholder="(11) 99999-9999"
              disabled={isLoading}
              style={{
                width: '100%',
                padding: 'var(--space-2) var(--space-3)',
                fontSize: 'var(--text-sm)',
                border: '1px solid var(--border-medium)',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                transition: 'border-color var(--transition-fast)',
                opacity: isLoading ? 0.5 : 1
              }}
            />
          </div>
          
          <div>
            <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
              Empresa
            </label>
            <input
              type="text"
              name="company"
              value={formData.company}
              onChange={handleChange}
              placeholder="Nome da empresa"
              disabled={isLoading}
              style={{
                width: '100%',
                padding: 'var(--space-2) var(--space-3)',
                fontSize: 'var(--text-sm)',
                border: '1px solid var(--border-medium)',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                transition: 'border-color var(--transition-fast)',
                opacity: isLoading ? 0.5 : 1
              }}
            />
          </div>
        </div>
      </div>

      {/* Document Information */}
      <div>
        <h4 style={{
          fontSize: 'var(--text-sm)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--text-primary)',
          marginBottom: 'var(--space-4)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em'
        }}>
          Documento
        </h4>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--space-4)' }}>
          <div>
            <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
              Tipo
            </label>
            <select
              name="document_type"
              value={formData.document_type}
              onChange={handleChange}
              disabled={isLoading}
              style={{
                width: '100%',
                padding: 'var(--space-2) var(--space-3)',
                fontSize: 'var(--text-sm)',
                border: '1px solid var(--border-medium)',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.5 : 1
              }}
            >
              <option value="none">Nenhum</option>
              <option value="cpf">CPF</option>
              <option value="cnpj">CNPJ</option>
            </select>
          </div>
          
          <div style={{ gridColumn: 'span 2' }}>
            <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
              CPF/CNPJ
            </label>
            <input
              type="text"
              name="cpf_cnpj"
              value={formatCPF_CNPJ(formData.cpf_cnpj || '')}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '')
                handleChange({ ...e, target: { ...e.target, name: 'cpf_cnpj', value } })
              }}
              placeholder="000.000.000-00"
              disabled={isLoading || formData.document_type === 'none'}
              style={{
                width: '100%',
                padding: 'var(--space-2) var(--space-3)',
                fontSize: 'var(--text-sm)',
                border: `1px solid ${errors.cpf_cnpj ? 'var(--color-error-500)' : 'var(--border-medium)'}`,
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                transition: 'border-color var(--transition-fast)',
                opacity: isLoading || formData.document_type === 'none' ? 0.5 : 1
              }}
            />
            {errors.cpf_cnpj && (
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-error-500)', marginTop: 'var(--space-1)' }}>
                {errors.cpf_cnpj}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Address */}
      <div>
        <h4 style={{
          fontSize: 'var(--text-sm)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--text-primary)',
          marginBottom: 'var(--space-4)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em'
        }}>
          Endereço
        </h4>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--space-4)' }}>
          <div>
            <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
              CEP
            </label>
            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
              <input
                type="text"
                name="cep"
                value={formData.cep}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 8)
                  handleChange({ ...e, target: { ...e.target, name: 'cep', value } })
                }}
                placeholder="00000-000"
                disabled={isLoading}
                style={{
                  flex: 1,
                  padding: 'var(--space-2) var(--space-3)',
                  fontSize: 'var(--text-sm)',
                  border: '1px solid var(--border-medium)',
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: 'var(--bg-primary)',
                  color: 'var(--text-primary)',
                  transition: 'border-color var(--transition-fast)',
                  opacity: isLoading ? 0.5 : 1
                }}
              />
              <button
                type="button"
                onClick={handleCepSearch}
                disabled={isLoading || !formData.cep}
                style={{
                  padding: 'var(--space-2) var(--space-4)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--color-primary-600)',
                  backgroundColor: 'var(--color-primary-600)',
                  color: 'var(--text-inverted)',
                  cursor: isLoading || !formData.cep ? 'not-allowed' : 'pointer',
                  transition: 'all var(--transition-base)',
                  opacity: isLoading || !formData.cep ? 0.5 : 1
                }}
              >
                Buscar
              </button>
            </div>
          </div>
          
          <div style={{ gridColumn: 'span 3' }}>
            <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
              Logradouro
            </label>
            <input
              type="text"
              name="logradouro"
              value={formData.logradouro}
              onChange={handleChange}
              placeholder="Rua, Avenida, etc."
              disabled={isLoading}
              style={{
                width: '100%',
                padding: 'var(--space-2) var(--space-3)',
                fontSize: 'var(--text-sm)',
                border: '1px solid var(--border-medium)',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                transition: 'border-color var(--transition-fast)',
                opacity: isLoading ? 0.5 : 1
              }}
            />
          </div>
          
          <div>
            <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
              Número
            </label>
            <input
              type="text"
              name="numero"
              value={formData.numero}
              onChange={handleChange}
              placeholder="123"
              disabled={isLoading}
              style={{
                width: '100%',
                padding: 'var(--space-2) var(--space-3)',
                fontSize: 'var(--text-sm)',
                border: '1px solid var(--border-medium)',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                transition: 'border-color var(--transition-fast)',
                opacity: isLoading ? 0.5 : 1
              }}
            />
          </div>
          
          <div>
            <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
              Complemento
            </label>
            <input
              type="text"
              name="complemento"
              value={formData.complemento}
              onChange={handleChange}
              placeholder="Apto, Bloco"
              disabled={isLoading}
              style={{
                width: '100%',
                padding: 'var(--space-2) var(--space-3)',
                fontSize: 'var(--text-sm)',
                border: '1px solid var(--border-medium)',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                transition: 'border-color var(--transition-fast)',
                opacity: isLoading ? 0.5 : 1
              }}
            />
          </div>
          
          <div>
            <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
              Bairro
            </label>
            <input
              type="text"
              name="bairro"
              value={formData.bairro}
              onChange={handleChange}
              placeholder="Bairro"
              disabled={isLoading}
              style={{
                width: '100%',
                padding: 'var(--space-2) var(--space-3)',
                fontSize: 'var(--text-sm)',
                border: '1px solid var(--border-medium)',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                transition: 'border-color var(--transition-fast)',
                opacity: isLoading ? 0.5 : 1
              }}
            />
          </div>
          
          <div>
            <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
              Cidade
            </label>
            <input
              type="text"
              name="cidade"
              value={formData.cidade}
              onChange={handleChange}
              placeholder="Cidade"
              disabled={isLoading}
              style={{
                width: '100%',
                padding: 'var(--space-2) var(--space-3)',
                fontSize: 'var(--text-sm)',
                border: '1px solid var(--border-medium)',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                transition: 'border-color var(--transition-fast)',
                opacity: isLoading ? 0.5 : 1
              }}
            />
          </div>
          
          <div>
            <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
              Estado
            </label>
            <input
              type="text"
              name="estado"
              value={formData.estado}
              onChange={(e) => {
                const value = e.target.value.toUpperCase().slice(0, 2)
                handleChange({ ...e, target: { ...e.target, name: 'estado', value } })
              }}
              placeholder="SP"
              disabled={isLoading}
              style={{
                width: '100%',
                padding: 'var(--space-2) var(--space-3)',
                fontSize: 'var(--text-sm)',
                border: '1px solid var(--border-medium)',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                transition: 'border-color var(--transition-fast)',
                opacity: isLoading ? 0.5 : 1
              }}
            />
          </div>
        </div>
      </div>

      {/* Lead Details */}
      <div>
        <h4 style={{
          fontSize: 'var(--text-sm)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--text-primary)',
          marginBottom: 'var(--space-4)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em'
        }}>
          Detalhes do Lead
        </h4>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--space-4)' }}>
          <div>
            <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
              Origem
            </label>
            <select
              name="source"
              value={formData.source}
              onChange={handleChange}
              disabled={isLoading}
              style={{
                width: '100%',
                padding: 'var(--space-2) var(--space-3)',
                fontSize: 'var(--text-sm)',
                border: '1px solid var(--border-medium)',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.5 : 1
              }}
            >
              <option value="website">Website</option>
              <option value="facebook">Facebook</option>
              <option value="instagram">Instagram</option>
              <option value="linkedin">LinkedIn</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="referral">Indicação</option>
              <option value="event">Evento</option>
              <option value="email">Email</option>
              <option value="phone">Telefone</option>
              <option value="other">Outro</option>
            </select>
          </div>
          
          <div>
            <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
              Status
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              disabled={isLoading}
              style={{
                width: '100%',
                padding: 'var(--space-2) var(--space-3)',
                fontSize: 'var(--text-sm)',
                border: '1px solid var(--border-medium)',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.5 : 1
              }}
            >
              <option value="new">Novo</option>
              <option value="contacted">Contatado</option>
              <option value="qualified">Qualificado</option>
              <option value="proposal">Proposta Enviada</option>
              <option value="negotiation">Negociação</option>
              <option value="won">Ganho</option>
              <option value="lost">Perdido</option>
              <option value="archived">Arquivado</option>
            </select>
          </div>
          
          <div>
            <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
              Prioridade
            </label>
            <select
              name="priority"
              value={formData.priority}
              onChange={handleChange}
              disabled={isLoading}
              style={{
                width: '100%',
                padding: 'var(--space-2) var(--space-3)',
                fontSize: 'var(--text-sm)',
                border: '1px solid var(--border-medium)',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.5 : 1
              }}
            >
              <option value="low">Baixa</option>
              <option value="medium">Média</option>
              <option value="high">Alta</option>
              <option value="urgent">Urgente</option>
            </select>
          </div>
        </div>
      </div>

      {/* Value and Probability */}
      <div>
        <h4 style={{
          fontSize: 'var(--text-sm)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--text-primary)',
          marginBottom: 'var(--space-4)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em'
        }}>
          Valor e Probabilidade
        </h4>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'var(--space-4)' }}>
          <div>
            <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
              Valor Estimado (R$)
            </label>
            <input
              type="number"
              name="estimated_value"
              value={formData.estimated_value || ''}
              onChange={handleChange}
              placeholder="0.00"
              step="0.01"
              disabled={isLoading}
              style={{
                width: '100%',
                padding: 'var(--space-2) var(--space-3)',
                fontSize: 'var(--text-sm)',
                border: '1px solid var(--border-medium)',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                transition: 'border-color var(--transition-fast)',
                opacity: isLoading ? 0.5 : 1
              }}
            />
          </div>
          
          <div>
            <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
              Probabilidade (%)
            </label>
            <input
              type="number"
              name="probability"
              value={formData.probability || ''}
              onChange={handleChange}
              placeholder="0-100"
              min="0"
              max="100"
              disabled={isLoading}
              style={{
                width: '100%',
                padding: 'var(--space-2) var(--space-3)',
                fontSize: 'var(--text-sm)',
                border: '1px solid var(--border-medium)',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                transition: 'border-color var(--transition-fast)',
                opacity: isLoading ? 0.5 : 1
              }}
            />
          </div>
        </div>
      </div>

      {/* Custom Fields */}
      {customFieldDefs.length > 0 && (
        <div>
          <h4 style={{
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-weight-semibold)',
            color: 'var(--text-primary)',
            marginBottom: 'var(--space-4)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            Campos Personalizados
          </h4>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'var(--space-4)' }}>
            {customFieldDefs.map((field: any) => {
              const value = (formData.custom_fields as Record<string, any>)?.[field.name] ?? ''
              const inputStyle = {
                width: '100%',
                padding: 'var(--space-2) var(--space-3)',
                fontSize: 'var(--text-sm)',
                border: '1px solid var(--border-medium)',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                transition: 'border-color var(--transition-fast)',
                opacity: isLoading ? 0.5 : 1
              }
              const labelEl = (
                <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
                  {field.label}{field.required && <span style={{ color: 'var(--color-error-500)', marginLeft: 2 }}>*</span>}
                </label>
              )

              if (field.field_type === 'checkbox') {
                return (
                  <div key={field.name} style={{ gridColumn: 'span 2', display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                    <input
                      type="checkbox"
                      checked={!!value}
                      onChange={e => handleCustomFieldChange(field.name, e.target.checked)}
                      disabled={isLoading}
                      style={{ width: '16px', height: '16px', accentColor: 'var(--color-primary-600)', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)' }}>
                      {field.label}
                    </span>
                  </div>
                )
              }

              if (field.field_type === 'textarea') {
                return (
                  <div key={field.name} style={{ gridColumn: 'span 2' }}>
                    {labelEl}
                    <textarea
                      value={value}
                      onChange={e => handleCustomFieldChange(field.name, e.target.value)}
                      placeholder={field.placeholder || ''}
                      rows={3}
                      disabled={isLoading}
                      style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
                    />
                  </div>
                )
              }

              if (field.field_type === 'select') {
                const options: string[] = field.options || []
                return (
                  <div key={field.name}>
                    {labelEl}
                    <select
                      value={value}
                      onChange={e => handleCustomFieldChange(field.name, e.target.value)}
                      disabled={isLoading}
                      style={{ ...inputStyle, cursor: isLoading ? 'not-allowed' : 'pointer' }}
                    >
                      <option value="">Selecione...</option>
                      {options.map((opt: string) => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                )
              }

              if (field.field_type === 'multiselect') {
                const options: string[] = field.options || []
                const selected: string[] = Array.isArray(value) ? value : []
                return (
                  <div key={field.name} style={{ gridColumn: 'span 2' }}>
                    {labelEl}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                      {options.map((opt: string) => (
                        <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer', fontSize: 'var(--text-sm)', color: 'var(--text-primary)' }}>
                          <input
                            type="checkbox"
                            checked={selected.includes(opt)}
                            onChange={e => {
                              const next = e.target.checked
                                ? [...selected, opt]
                                : selected.filter(v => v !== opt)
                              handleCustomFieldChange(field.name, next)
                            }}
                            disabled={isLoading}
                            style={{ accentColor: 'var(--color-primary-600)' }}
                          />
                          {opt}
                        </label>
                      ))}
                    </div>
                  </div>
                )
              }

              if (field.field_type === 'currency') {
                return (
                  <div key={field.name}>
                    {labelEl}
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 'var(--space-3)', top: '50%', transform: 'translateY(-50%)', fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', pointerEvents: 'none' }}>R$</span>
                      <input
                        type="number"
                        value={value}
                        onChange={e => handleCustomFieldChange(field.name, e.target.value ? parseFloat(e.target.value) : '')}
                        placeholder="0,00"
                        step="0.01"
                        disabled={isLoading}
                        style={{ ...inputStyle, paddingLeft: 'calc(var(--space-3) + 24px)' }}
                      />
                    </div>
                  </div>
                )
              }

              // text, number, date, email, phone, url
              const typeMap: Record<string, string> = {
                text: 'text', number: 'number', date: 'date',
                email: 'email', phone: 'tel', url: 'url'
              }
              return (
                <div key={field.name}>
                  {labelEl}
                  <input
                    type={typeMap[field.field_type] || 'text'}
                    value={value}
                    onChange={e => handleCustomFieldChange(field.name, field.field_type === 'number' ? (e.target.value ? parseFloat(e.target.value) : '') : e.target.value)}
                    placeholder={field.placeholder || ''}
                    disabled={isLoading}
                    style={inputStyle}
                  />
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Advanced Options */}
      <div>
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          style={{
            width: '100%',
            padding: 'var(--space-2) var(--space-4)',
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-weight-medium)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border-medium)',
            backgroundColor: 'transparent',
            color: 'var(--text-secondary)',
            cursor: 'pointer',
            transition: 'all var(--transition-base)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'var(--space-2)'
          }}
        >
          {showAdvanced ? '−' : '+'} Opções Avançadas
        </button>
        
        {showAdvanced && (
          <div style={{ marginTop: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            {/* Dates */}
            <div>
              <h5 style={{
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--text-primary)',
                marginBottom: 'var(--space-4)'
              }}>
                Datas Importantes
              </h5>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'var(--space-4)' }}>
                <div>
                  <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
                    Data do Primeiro Contato
                  </label>
                  <input
                    type="datetime-local"
                    name="first_contact_date"
                    value={formData.first_contact_date || ''}
                    onChange={handleChange}
                    disabled={isLoading}
                    style={{
                      width: '100%',
                      padding: 'var(--space-2) var(--space-3)',
                      fontSize: 'var(--text-sm)',
                      border: '1px solid var(--border-medium)',
                      borderRadius: 'var(--radius-md)',
                      backgroundColor: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      transition: 'border-color var(--transition-fast)',
                      opacity: isLoading ? 0.5 : 1
                    }}
                  />
                </div>
                
                <div>
                  <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
                    Data de Follow-up
                  </label>
                  <input
                    type="datetime-local"
                    name="follow_up_date"
                    value={formData.follow_up_date || ''}
                    onChange={handleChange}
                    disabled={isLoading}
                    style={{
                      width: '100%',
                      padding: 'var(--space-2) var(--space-3)',
                      fontSize: 'var(--text-sm)',
                      border: '1px solid var(--border-medium)',
                      borderRadius: 'var(--radius-md)',
                      backgroundColor: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      transition: 'border-color var(--transition-fast)',
                      opacity: isLoading ? 0.5 : 1
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Social Media */}
            <div>
              <h5 style={{
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--text-primary)',
                marginBottom: 'var(--space-4)'
              }}>
                Redes Sociais
              </h5>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--space-4)' }}>
                <div>
                  <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
                    Instagram
                  </label>
                  <input
                    type="text"
                    name="instagram_handle"
                    value={formData.instagram_handle}
                    onChange={handleChange}
                    placeholder="@usuario"
                    disabled={isLoading}
                    style={{
                      width: '100%',
                      padding: 'var(--space-2) var(--space-3)',
                      fontSize: 'var(--text-sm)',
                      border: '1px solid var(--border-medium)',
                      borderRadius: 'var(--radius-md)',
                      backgroundColor: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      transition: 'border-color var(--transition-fast)',
                      opacity: isLoading ? 0.5 : 1
                    }}
                  />
                </div>
                
                <div>
                  <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
                    Facebook
                  </label>
                  <input
                    type="url"
                    name="facebook_url"
                    value={formData.facebook_url}
                    onChange={handleChange}
                    placeholder="https://facebook.com/..."
                    disabled={isLoading}
                    style={{
                      width: '100%',
                      padding: 'var(--space-2) var(--space-3)',
                      fontSize: 'var(--text-sm)',
                      border: '1px solid var(--border-medium)',
                      borderRadius: 'var(--radius-md)',
                      backgroundColor: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      transition: 'border-color var(--transition-fast)',
                      opacity: isLoading ? 0.5 : 1
                    }}
                  />
                </div>
                
                <div>
                  <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
                    LinkedIn
                  </label>
                  <input
                    type="url"
                    name="linkedin_url"
                    value={formData.linkedin_url}
                    onChange={handleChange}
                    placeholder="https://linkedin.com/in/..."
                    disabled={isLoading}
                    style={{
                      width: '100%',
                      padding: 'var(--space-2) var(--space-3)',
                      fontSize: 'var(--text-sm)',
                      border: '1px solid var(--border-medium)',
                      borderRadius: 'var(--radius-md)',
                      backgroundColor: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      transition: 'border-color var(--transition-fast)',
                      opacity: isLoading ? 0.5 : 1
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
                Notas
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Adicione notas sobre este lead..."
                rows={4}
                disabled={isLoading}
                style={{
                  width: '100%',
                  padding: 'var(--space-2) var(--space-3)',
                  fontSize: 'var(--text-sm)',
                  border: '1px solid var(--border-medium)',
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: 'var(--bg-primary)',
                  color: 'var(--text-primary)',
                  transition: 'border-color var(--transition-fast)',
                  opacity: isLoading ? 0.5 : 1,
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Criar Negociação */}
      <div style={{ borderTop: '1px solid var(--border-light)', paddingTop: 'var(--space-4)' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', cursor: 'pointer', userSelect: 'none' }}>
          <input
            type="checkbox"
            checked={createDeal}
            onChange={e => setCreateDeal(e.target.checked)}
            disabled={isLoading}
            style={{ width: '16px', height: '16px', accentColor: 'var(--color-primary-600)', cursor: 'pointer' }}
          />
          <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-primary)' }}>
            Adicionar ao pipeline como negociação
          </span>
        </label>

        {createDeal && (
          <div style={{ marginTop: 'var(--space-4)', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'var(--space-4)' }}>
            <div>
              <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-secondary)', marginBottom: 'var(--space-2)' }}>
                Pipeline
              </label>
              <select
                value={selectedPipelineId || ''}
                onChange={e => setSelectedPipelineId(e.target.value ? Number(e.target.value) : null)}
                disabled={isLoading || pipelines.length === 0}
                style={{ width: '100%', padding: 'var(--space-2) var(--space-3)', fontSize: 'var(--text-sm)', border: '1px solid var(--border-medium)', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)' }}
              >
                <option value="">Selecione o pipeline</option>
                {pipelines.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-secondary)', marginBottom: 'var(--space-2)' }}>
                Etapa
              </label>
              <select
                value={selectedStageId || ''}
                onChange={e => setSelectedStageId(e.target.value ? Number(e.target.value) : null)}
                disabled={isLoading || stages.length === 0}
                style={{ width: '100%', padding: 'var(--space-2) var(--space-3)', fontSize: 'var(--text-sm)', border: '1px solid var(--border-medium)', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)' }}
              >
                <option value="">Selecione a etapa</option>
                {stages.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-secondary)', marginBottom: 'var(--space-2)' }}>
                Valor da negociação (R$)
              </label>
              <input
                type="number"
                value={dealValue}
                onChange={e => setDealValue(e.target.value)}
                placeholder="0.00"
                step="0.01"
                disabled={isLoading}
                style={{ width: '100%', padding: 'var(--space-2) var(--space-3)', fontSize: 'var(--text-sm)', border: '1px solid var(--border-medium)', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)' }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Criar Contato / Conversa */}
      <div style={{ borderTop: '1px solid var(--border-light)', paddingTop: 'var(--space-4)' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', cursor: 'pointer', userSelect: 'none' }}>
          <input
            type="checkbox"
            checked={createContact}
            onChange={e => { setCreateContact(e.target.checked); if (!e.target.checked) setStartConversation(false) }}
            disabled={isLoading || !formData.phone}
            style={{ width: '16px', height: '16px', accentColor: 'var(--color-primary-600)', cursor: 'pointer' }}
          />
          <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: formData.phone ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
            Criar contato de WhatsApp
            {!formData.phone && <span style={{ fontSize: 'var(--text-xs)', marginLeft: 'var(--space-2)', fontWeight: 'normal' }}>(informe o telefone acima)</span>}
          </span>
        </label>

        {createContact && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', cursor: 'pointer', userSelect: 'none', marginTop: 'var(--space-3)', marginLeft: 'var(--space-6)' }}>
            <input
              type="checkbox"
              checked={startConversation}
              onChange={e => setStartConversation(e.target.checked)}
              disabled={isLoading}
              style={{ width: '16px', height: '16px', accentColor: 'var(--color-primary-600)', cursor: 'pointer' }}
            />
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
              Iniciar conversa imediatamente
            </span>
          </label>
        )}
      </div>

      {/* Submit Button */}
      <div style={{ display: 'flex', gap: 'var(--space-4)', justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={() => window.history.back()}
          disabled={isLoading}
          style={{
            padding: 'var(--space-2) var(--space-4)',
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-weight-medium)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border-medium)',
            backgroundColor: 'transparent',
            color: 'var(--text-secondary)',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            transition: 'all var(--transition-base)',
            opacity: isLoading ? 0.5 : 1
          }}
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={isLoading}
          style={{
            padding: 'var(--space-2) var(--space-4)',
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-weight-medium)',
            borderRadius: 'var(--radius-md)',
            border: 'none',
            backgroundColor: 'var(--color-primary-600)',
            color: 'var(--text-inverted)',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            transition: 'all var(--transition-base)',
            opacity: isLoading ? 0.5 : 1
          }}
        >
          {isLoading ? 'Salvando...' : 'Salvar Lead'}
        </button>
      </div>
    </form>
  )
}

export default LeadForm
